import type { MenuItem } from 'primeng/api'
import { PermissionService } from '@core/auth/services/permission.service'
import { Router } from '@angular/router'

export interface ToolbarData {
  testId?: string;
  subtitle?: string;
  desktopOnly?: boolean;
}

export const getMenuItems = (
  onToggle: () => void,
  closeToolbarDrawer: () => void,
  permissions: PermissionService,
  router: Router,
  getInsuredUuid: () => string | null,
  getInsuredUuidFromRoute: () => string | null,
  getCertificateUuidFromRoute: () => string | null,
  openPreview: (certificateUuid: string) => void,
): MenuItem[] => [
  {
    label: 'Certificate',
    data: {
      testId: 'toolbar-menuitem-cerificate'
    },
    items: [
      {
        label: 'New Certificate',
        data: { testId: 'toolbar-menuitem-certificate-new' },
        command: () => {
          const insuredUuid = getInsuredUuid()
          void router.navigate(
            ['/certificate/create'],
            insuredUuid !== null ? { queryParams: { insuredUuid } } : {}
          )
          closeToolbarDrawer()
        },
      },
      {
        label: 'New Version',
        data: { testId: 'toolbar-menuitem-certificate-new-version' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Duplicate',
        data: { testId: 'toolbar-menuitem-certificate-duplicate' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Activation',
        data: { testId: 'toolbar-menuitem-certificate-activation' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Cancellation',
        data: { testId: 'toolbar-menuitem-certificate-cancellation' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Lessor Requirement',
        data: { testId: 'toolbar-menuitem-certificate-lessor-requirement' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Certificate for Restricted Countries',
        data: { testId: 'toolbar-menuitem-certificate-restricted-countries' },
        command: () => {
          closeToolbarDrawer()
        }
      }
    ],
    expanded: false
  },
  {
    label: 'Document',
    data: { testId: 'toolbar-menuitem-document' },
    items: [
      {
        label: 'Show Preview',
        data: { testId: 'toolbar-menuitem-document-show-preview' },
        command: () => {
          const certificateUuid = getCertificateUuidFromRoute()
          if (certificateUuid !== null) {
            openPreview(certificateUuid)
          }
          closeToolbarDrawer()
        },
        disabled: getCertificateUuidFromRoute() === null,
      },
      {
        label: 'Create Document',
        data: { testId: 'toolbar-menuitem-document-create' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Send Document',
        data: { testId: 'toolbar-menuitem-document-send' },
        command: () => {
          closeToolbarDrawer()
        }
      }
    ],
    expanded: false
  },
  {
    label: 'Certificate Holder',
    data: { testId: 'toolbar-menuitem-certificate-holder' },
    styleClass: 'no-submenu',
    command: () => {
      void router.navigate(['/certificate-holder'])
      closeToolbarDrawer()
    },
    disabled: !permissions.hasEditRights(),
  },
  {
    label: 'Reminder',
    data: { testId: 'toolbar-menuitem-reminder' },
    styleClass: 'no-submenu',
    command: () => {
      closeToolbarDrawer()
    }
  },
  {
    label: 'Renewal',
    data: { testId: 'toolbar-menuitem-renewal' },
    items: [
      {
        label: 'Renew Certificates',
        data: { testId: 'toolbar-menuitem-renew-certificates' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Create Certificate List',
        data: { testId: 'toolbar-menuitem-renewal-create-certificate-list' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Send Renewal Enquiry',
        data: { testId: 'toolbar-menuitem-renewal-send-enquiry' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Renewal Announcement',
        data: { testId: 'toolbar-menuitem-renewal-announcement' },
        command: () => {
          closeToolbarDrawer()
        }
      }

    ],
    expanded: false
  },
  {
    label: 'Insured',
    data: { testId: 'toolbar-menuitem-insured' },
    items: [
      {
        label: 'Import Insured',
        data: { testId: 'toolbar-menuitem-insured-import' },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Create New Insured',
        routerLink: ['/insured/create'],
        data: {
          testId: 'toolbar-menuitem-insured-create-new'
        },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Duplicate Insured',
        data: {
          testId: 'toolbar-menuitem-insured-duplicate'
        },
        command: () => {
          closeToolbarDrawer()
        },
        disabled: getInsuredUuidFromRoute() === null,
      },
      {
        label: 'Sync Insured',
        data: {
          testId: 'toolbar-menuitem-insured-sync-member',
          subtitle: 'Last updated:\n12.12.2025 03:24:59'
        },
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Add Overrides',
        data: { testId: 'toolbar-menuitem-insured-add-overrides' },
        command: () => {
          onToggle()
          closeToolbarDrawer()
        }
      }
    ],
    expanded: false
  },
  {
    label: 'Tools',
    data: { testId: 'toolbar-menuitem-tools' },
    items: [
      {
        label: 'Template Management',
        data: {
          testId: 'toolbar-menuitem-tools-template-management',
          desktopOnly: true
        },
        routerLink: ['/template'],
        disabled: !permissions.hasRedactRights(),
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Content Unit Management',
        data: {
          testId: 'toolbar-menuitem-tools-content-unit-management',
          desktopOnly: true
        },
        routerLink: ['/content-unit'],
        disabled: !permissions.hasRedactRights(),
        command: () => {
          closeToolbarDrawer()
        }
      },
      {
        label: 'Reports',
        data: { testId: 'toolbar-menuitem-tools-reports' },
        command: () => {
          closeToolbarDrawer()
        }
      }
    ],
    expanded: false
  },
  {
    label: 'Administration',
    data: { testId: 'toolbar-menuitem-administration' },
    items: [
      {
        label: 'Settings',
        data: { testId: 'toolbar-menuitem-administration-settings' },
        disabled: !permissions.hasAdminRights(),
        items: [
          {
            label: 'Default Values',
            data: { testId: 'toolbar-menuitem-administration-settings-default-values' },
            command: () => {
              closeToolbarDrawer()
            },
            disabled: !permissions.hasAdminRights(),
          },
          {
            label: 'Fleet List',
            data: { testId: 'toolbar-menuitem-administration-settings-fleet-list' },
            command: () => {
              closeToolbarDrawer()
            },
            disabled: !permissions.hasAdminRights(),
          },
          {
            label: 'Period Management',
            data: { testId: 'toolbar-menuitem-administration-settings-period-management' },
            command: () => {
              closeToolbarDrawer()
            },
            disabled: !permissions.hasAdminRights(),
          }
        ],
        expanded: false
      },
      {
        label: 'Change Log',
        data: { testId: 'toolbar-menuitem-administration-change-log' },
        command: () => {
          closeToolbarDrawer()
        },
        disabled: !permissions.hasAdminRights(),
      },
      {
        label: 'Sync All Insured',
        data: { testId: 'toolbar-menuitem-administration-sync-all-insured' },
        command: () => {
          closeToolbarDrawer()
        },
        disabled: !permissions.hasAdminRights(),
      }
    ],
    expanded: false
  }
]
